import { Injectable, Injector } from "@angular/core";
import { collection, collectionChanges, Firestore } from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { map, skip, Subject, switchMap, takeUntil } from "rxjs";
import { BaseService } from "../app.base.service";
import { AccountRepositoryService } from "../pages/account/account-repository.service";
import { CommonService } from "./common/common.service";
import { PaymentDataService } from "./payment-data.service";

@Injectable({
  providedIn: "root",
})
export class PaymentCardService extends BaseService {
  private unsubscribe$ = new Subject<void>();

  constructor(
    private firestore: Firestore,
    private injector: Injector,
    private authService: AuthService,
    private accountService: AccountRepositoryService,
    private paymentDataService: PaymentDataService,
    private commonService: CommonService
  ) {
    super(injector);
  }

  setSelectedCard(purchaseDetail: Data): string | undefined {
    const truevoId = "22";
    const checkoutId = "21";

    const configuredPType = purchaseDetail?.p_type ? String(purchaseDetail.p_type) : "";
    if (configuredPType) {
      return configuredPType;
    }

    const truevo = this.authService?.truevoDetails$?.value;
    if (truevo && truevo?.prosubs?.includes(purchaseDetail?.prosub_id)) {
      return truevoId;
    }

    const checkout = this.paymentDataService?.checkoutDetails$?.value;
    if (checkout && checkout?.prosubs?.includes(purchaseDetail?.prosub_id)) {
      return checkoutId;
    }

    return;
  }

  /*
   This function is used to change the check card
   It takes in the stored cards and the index
   It then maps through the stored cards and changes the check card to true
   and the other cards to false 
   */
  getCheckedCard(storedCards, index) {
    if (storedCards.length === 1) {
      setTimeout(() => {
        storedCards[index].isChecked = true;
      }, 100);
      return;
    }

    storedCards = storedCards.map((card, cIndex) => {
      if (index === cIndex) {
        card.isChecked = true;
      } else {
        card.isChecked = false;
      }
      return card;
    });
    const checkedCard = storedCards.find((card) => card.isChecked);

    return checkedCard;
  }

  /**
   * The function `getDistinctCards` processes stored card data to return distinct cards, along with a
   * selected card type based on a checked card.
   * @returns The function `getDistinctCards` returns an array containing three elements:
   * 1. The `distinct` array which contains unique card objects based on the `card_short_number`
   * property.
   * 2. The `storedCards` array with updated `card_expiry_expiry` property values.
   * 3. The `selectedCard` value which is either "15" or "10" based on the checked card type being "
   */
  getDistinctCards(storedCards: Data) {
    let checkedCard = storedCards.find((card) => card.isChecked === true);

    // Add null/undefined validation to prevent "undefined is not an object" error
    // Check if checkedCard exists before accessing card_type property
    let cardType;
    if (checkedCard && checkedCard.card_type) {
      cardType = checkedCard.card_type;
    }

    let selectedCard;
    // Use cardType instead of checkedCard, and add null check
    if (cardType?.toLowerCase() === "master" || cardType?.toLowerCase() === "mastercard") {
      selectedCard = "15";
    } else {
      selectedCard = "10";
    }

    storedCards.map((cards) => {
      return (cards["card_expiry_expiry"] = new Date(
        "1/" + cards.expiry_date.split("/")[0] + "/" + cards.expiry_date.split("/")[1]
      ));
    });
    let distinct = [];
    if (distinct.length === 0) {
      distinct.push(storedCards[0]);
    }

    storedCards.map((data) => {
      if (!distinct.some((f) => f?.card_short_number === data?.card_short_number)) {
        distinct.push(data);
      }
    });
    return [distinct, storedCards, selectedCard];
  }

  /**
   * The function `setCardAsPrimaryMethod` retrieves a user's stored card, updates it as the primary
   * payment method, and displays corresponding success or error messages.
   */
  setCardAsPrimaryMethod(): void {
    this.commonService
      .getUid("PaymentCardService")
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap((userId) => {
          if (!userId) {
            return; // No userId, stop processing
          }

          const storedCardRef = collection(this.firestore, `users/${userId}/stored_card_V1`);
          return collectionChanges(storedCardRef).pipe(
            skip(1), // Skip initial snapshot
            map((actions: any[]) =>
              actions.map((a) => ({ id: a.doc.id, ...a.doc.data() }))
            )
          );
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (cards: any[]) => {
          if (!cards?.length) return;

          const card = cards[0];
          if (!card?.encrypt_card_id) return;

          this.accountService
            .updatePrimaryCard(card.encrypt_card_id)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
              next: (data) => {
                if (data?.status === "rejected") {
                  this.authService
                    .getConstantsMsg(data?.error_code)
                    .then((translatedMsg) => {
                      const defaultMsg = {
                        message: { line_1: "Something went wrong!", line_2: "" },
                        style: { icon: "tick circle error.svg", type: "Error" },
                      };
                      this.authService.showToastMessage(translatedMsg, defaultMsg);
                    });
                } else if (data?.status === "success") {
                  this.authService.getConstantsMsg("1021").then((translatedMsg) => {
                    const defaultMsg = {
                      message: { line_1: "Primary payment method updated!", line_2: "" },
                      style: { icon: "tick circle success.png", type: "Success" },
                    };
                    this.authService.showToastMessage(translatedMsg, defaultMsg);
                  });
                } else {
                  const translatedMsg = {
                    message: { line_1: data?.response?.message ?? data?.message, line_2: "" },
                    style: { duration: 2000, icon: "tick circle error.svg", type: "Error" },
                  };
                  const defaultMsg = {
                    message: { line_1: "Something went wrong!", line_2: "" },
                    style: { icon: "tick circle error.svg", type: "Error" },
                  };
                  this.authService.showToastMessage(translatedMsg, defaultMsg);
                }
              },
              error: (err) => {
                console.error("Error updating primary card:", err);
              },
            });
        },
        error: (err) => {
          console.error("Error processing collection changes:", err);
        },
      });
  }


  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
